import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { notasVentaApi } from '../api/notasVenta'
import { listasApi } from '../api/listaPrecios'
import api from '../api/client'
import EstadoBadge from '../components/EstadoBadge'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import { useStoredState } from '../hooks/useStoredState'
import { useAuth } from '../context/AuthContext'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import useDebounce from '../hooks/useDebounce'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'

// Abreviaturas del estado de despacho del remito (mini-tag junto al estado de
// la NV — el nombre completo viaja en el tooltip). Códigos de estado_vigente.
const REMITO_ABREV = {
  ingresado: 'ING',
  disponible: 'DISP',
  en_armado: 'ARM',
  en_calle: 'DIST',
  entregado: 'ENT',
  recuperado: 'REC',
  fallo_sin_reasignar: 'FALLO',
  falta_despachar: 'FALTA',
  siniestro: 'SINI',
  anulado: 'ANUL',
  retira: 'RET',
}
import Boton from '../components/Boton'
import LinkDoc from '../components/LinkDoc'
import { useFilaLink } from '../hooks/useFilaLink'
import { colors, font, text, space, radius } from '../design/tokens'
import Paginador from '../components/Paginador'
import ModalImportarPresea from '../components/ModalImportarPresea'

const fmtMoney = (n) => n == null || isNaN(Number(n))
  ? '—'
  : '$' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function PagoDot({ estado, nv }) {
  if (!estado) return <span style={{ color: '#cbd5e1' }}>—</span>
  // Pago PARCIAL (sugerencia #76): lo cobrado no cubre el monto a cobrar — se
  // dice con los números, no como un color más.
  const parcial = nv && nv.monto_a_cobrar != null
    ? `Pago PARCIAL: cobrado $${fmtMonto(nv.pagado || 0)} de $${fmtMonto(nv.monto_a_cobrar)} — falta $${fmtMonto(nv.saldo ?? (nv.monto_a_cobrar - (nv.pagado || 0)))}`
    : 'Pago PARCIAL: lo cobrado no cubre el total'
  const map = {
    confirmado: { color: '#16a34a', label: 'Pago confirmado (cubre el total)' },
    pendiente:  { color: '#eab308', label: 'Pago pendiente (transferencia/convenir)' },
    parcial:    { color: '#f97316', label: parcial },
    rechazado:  { color: '#dc2626', label: 'PAGO RECHAZADO (Mobbex) — recuperar cobro con otra forma de pago' },
    // Sug #131: devuelta entera (mercadería y plata) — no se debe nada.
    devuelta:   { color: '#64748b', label: 'DEVUELTA: se devolvió la venta y se reintegró la plata — no se debe nada' },
    a_favor:    { color: '#7c3aed', label: 'A FAVOR DEL CLIENTE: se cobró más de lo que quedó después de la devolución' },
  }
  // Membresía del Wine Club que no se pudo cobrar (11 oct 2026): mismo círculo
  // rojo, con lo que de verdad pasa — LGR reintenta y lo pasa a verde solo.
  if (estado === 'rechazado' && nv?.origen === 'wine_club') {
    map.rechazado = { color: '#dc2626', label: 'COBRO DE LA MEMBRESÍA FALLIDO — LGR lo reintenta cada 3 días; cuando entra, pasa a verde solo' }
  }
  const m = map[estado] || { color: '#94a3b8', label: estado }
  return (
    <span
      title={m.label}
      style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: m.color }}
    />
  )
}

/**
 * Los tags redondos del ciclo de comprobantes — la lectura rápida del vendedor
 * de qué tarea le falta a cada pedido:
 *   F = FACTURADO (factura fiscal emitida en Presea)
 *   P = PAGADO    (recibo acreditado en Presea)
 *   R = REMITIDO  (remito Presea emitido)
 * Cada uno se "llena" con la emisión de su comprobante; con los 3 completos la
 * fila muestra un único C = COMPLETO.
 */
const TAG_CICLO = {
  f: { letra: 'F', color: '#1d4ed8', on: 'FACTURADO', off: 'Falta facturar' },
  p: { letra: 'P', color: '#16a34a', on: 'PAGADO (recibo acreditado)', off: 'Falta el recibo (cobro acreditado)' },
  r: { letra: 'R', color: '#6d28d9', on: 'REMITIDO', off: 'Falta el remito' },
}

function TagRedondo({ letra, color, lleno, title }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: '50%',
        fontSize: 10, fontWeight: 700, lineHeight: 1,
        ...(lleno
          ? { background: color, color: '#fff', border: `1px solid ${color}` }
          : { background: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0' }),
      }}
    >
      {letra}
    </span>
  )
}

function CicloTags({ ciclo }) {
  if (!ciclo) return <span style={{ color: '#cbd5e1' }}>—</span>
  if (ciclo.completo) {
    return <TagRedondo letra="C" color="#166534" lleno
      title={ciclo.sin_factura
        ? 'COMPLETO: solo remito (sin factura ni recibo — envío de mercadería)'
        : ciclo.sin_recibo
          ? 'COMPLETO: factura + remito (marcada SIN RECIBO — no lleva cobro)'
          : 'COMPLETO: factura + recibo + remito emitidos'} />
  }
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {['f', 'p', 'r'].map(k => (
        <TagRedondo key={k} letra={TAG_CICLO[k].letra} color={TAG_CICLO[k].color}
          lleno={!!ciclo[k]}
          title={k === 'f' && ciclo.sin_factura ? 'No lleva factura (solo remito)'
            : k === 'p' && ciclo.sin_recibo ? 'No lleva recibo (marcada sin cobro)'
            : (ciclo[k] ? TAG_CICLO[k].on : TAG_CICLO[k].off)} />
      ))}
    </span>
  )
}

const ESTADO_OPCIONES = [
  { value: 'borrador',  label: 'Borrador',  bg: '#f1f5f9', color: '#475569' },
  { value: 'procesado', label: 'Procesado', bg: '#dcfce7', color: '#166534' },
  { value: 'frenado',   label: 'Frenado',   bg: '#fee2e2', color: '#991b1b' },
]

const FILTROS_DEFAULT = { estados: [], fecha_desde: '', fecha_hasta: '', q: '', articulo: '', entrega: '', email: '', ip: '', sin_completos: false, con_saldo: false, vendedor_id: '' }
// `lista` arranca sin definir a propósito: al montar se defaultea a la lista
// del USUARIO (su canal) — Limpiar vuelve a ese default, no a "Todas".

/**
 * Una fila del listado. Se separó en componente porque el comportamiento de
 * "abrir en pestaña nueva" vive en un hook, y los hooks no se pueden llamar
 * dentro de un map.
 */
function FilaNV({ nv, sel, styles, children }) {
  const fila = useFilaLink(`/notas-venta/${nv.id}`)
  // RECUPERADA por el circuito (pagó / se hizo socio / cargó comprobante) y
  // pendiente de trabajo: VERDE y primera de la lista (Martin, 8 sep 2026).
  const verde = nv.recuperada ? { background: '#f0fdf4', boxShadow: 'inset 3px 0 0 #22c55e' } : {}
  const fondoBase = nv.recuperada ? '#f0fdf4' : ''

  return (
    <tr
      {...fila}
      onMouseEnter={e => { e.currentTarget.style.background = colors.bgSoft }}
      onMouseLeave={e => { e.currentTarget.style.background = sel.has(nv.id) ? colors.linkBg : fondoBase }}
      style={{ ...styles.tr, ...verde, ...(sel.has(nv.id) ? styles.trSel : {}), ...fila.style }}
    >
      {children}
    </tr>
  )
}

export default function NotasVentaPage() {
  const { rolOperativoVentas, user } = useAuth()
  const navigate = useNavigate()
  const [nvs, setNvs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.nv', FILTROS_DEFAULT)
  const [importarPresea, setImportarPresea] = useState(false)
  const [paginacion, setPaginacion] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [verArchivadas, setVerArchivadas] = useState(false)
  // Solapa 🤖 (circuito de Martin, 8 sep): el pedido sin pagar lo trabajan en
  // paralelo el vendedor y el sistema. "Dejar al sistema" lo saca del listado
  // del vendedor; los mails SIGUEN. Si paga vuelve solo; a las 72 h se archiva.
  const [verDejadas, setVerDejadas] = useState(false)
  const [dejadasCount, setDejadasCount] = useState(0)
  const [sel, setSel] = useState(() => new Set())   // ids seleccionados (archivar/desarchivar)
  const [accionMsg, setAccionMsg] = useState('')

  // Saneo: descarta estados guardados que ya no estén en las opciones (ej:
  // "pendiente_envio" o "enviado" que sacamos del filtro de NV).
  useEffect(() => {
    const validos = ESTADO_OPCIONES.map(o => o.value)
    const limpio = filtros.estados.filter(e => validos.includes(e))
    if (limpio.length !== filtros.estados.length) {
      setFiltros(f => ({ ...f, estados: limpio }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filtro de CANAL (lista de precios del cliente): el vendedor arranca viendo
  // su propia lista para no empastarse con las NV de otro canal; puede abrir
  // "Todas" cuando lo necesite y la elección persiste hasta Limpiar.
  const [listas, setListas] = useState([])
  useEffect(() => {
    listasApi.listar().then(({ data }) => setListas(data.data ?? data)).catch(() => {})
  }, [])
  useEffect(() => {
    if (filtros.lista === undefined && user) {
      setFiltros(f => ({ ...f, lista: user.lista_precios || '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filtros.lista])

  // Filtro por vendedor (sug #92): los usuarios de LGR que venden.
  const [vendedores, setVendedores] = useState([])
  useEffect(() => {
    api.get('/usuarios/vendedores').then(({ data }) => setVendedores(Array.isArray(data) ? data : (data.data || []))).catch(() => {})
  }, [])

  // Buscar sin martillar: el input escribe filtros.q en vivo, el fetch usa el
  // valor debounced (350 ms — mismo criterio que ClientesPage). Y la tabla no
  // se blanquea en cada cambio: "Cargando..." solo si todavía no hay filas
  // (keep-previous-data, auditoría 1 jul 2026).
  const qBuscar = useDebounce(filtros.q, 350)
  // Producto y entrega (sug #63): mismo debounce que la búsqueda general.
  const artBuscar = useDebounce(filtros.articulo, 350)
  const entregaBuscar = useDebounce(filtros.entrega, 350)
  // Email e IP del comprador (sug #64): el caso de uso es rastrear fraude —
  // todos los pedidos que salieron del mismo mail o de la misma IP.
  const emailBuscar = useDebounce(filtros.email, 350)
  const ipBuscar = useDebounce(filtros.ip, 350)

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const params = { page }
      if (qBuscar) params.q = qBuscar
      if (artBuscar) params.articulo = artBuscar
      if (entregaBuscar) params.entrega = entregaBuscar
      if (emailBuscar) params.email = emailBuscar
      if (ipBuscar) params.ip = ipBuscar
      if (filtros.estados.length > 0) params.estados = filtros.estados.join(',')
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta
      if (filtros.sin_completos) params.sin_completos = 1
      if (filtros.con_saldo) params.pago = 'parcial'
      if (filtros.lista) params.lista = filtros.lista
      if (filtros.vendedor_id) params.vendedor_id = filtros.vendedor_id
      if (verArchivadas) params.archivados = 1
      if (verDejadas && !verArchivadas) params.dejadas = 1
      const { data } = await notasVentaApi.listar(params)
      setNvs(data.data)
      if (data.dejadas_count !== undefined) setDejadasCount(data.dejadas_count)
      setPaginacion({ current_page: data.current_page, last_page: data.last_page, total: data.total })
      setError(null)
    } catch {
      setError('No se pudieron cargar las notas de venta. Reintentá o limpiá los filtros.')
    } finally { if (!silent) setLoading(false) }
  }, [qBuscar, artBuscar, entregaBuscar, emailBuscar, ipBuscar, filtros.estados, filtros.fecha_desde, filtros.fecha_hasta, filtros.sin_completos, filtros.con_saldo, filtros.lista, filtros.vendedor_id, page, verArchivadas, verDejadas])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  // La selección se limpia al navegar (los ids viejos ya no aplican).
  const limpiarSel = () => setSel(new Set())

  // Solo los borradores se pueden archivar; en la vista de archivadas, todas
  // (son borradores archivados) se pueden desarchivar.
  const esSeleccionable = (nv) => verArchivadas || nv.estado === 'borrador'
  const seleccionables = nvs.filter(esSeleccionable)
  const todasSel = seleccionables.length > 0 && seleccionables.every(nv => sel.has(nv.id))

  const toggleSel = (id) => setSel(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const toggleTodas = () => setSel(s => {
    if (seleccionables.every(nv => s.has(nv.id))) return new Set()
    return new Set(seleccionables.map(nv => nv.id))
  })

  const dejar = async () => {
    if (sel.size === 0) return
    setAccionMsg('')
    try {
      const { data } = await notasVentaApi.dejar([...sel])
      setSel(new Set())
      setAccionMsg(`${data.dejadas} dejada${data.dejadas === 1 ? '' : 's'} al sistema.` + (data.omitidas ? ` ${data.omitidas} sin persecución activa no aplicaban.` : ''))
      await cargar()
    } catch { setAccionMsg('No se pudo dejar al sistema.') }
  }
  const retomar = async () => {
    if (sel.size === 0) return
    setAccionMsg('')
    try {
      const { data } = await notasVentaApi.retomar([...sel])
      setSel(new Set())
      setAccionMsg(`${data.retomadas} de vuelta en tu listado (los mails nunca pararon).`)
      await cargar()
    } catch { setAccionMsg('No se pudo retomar.') }
  }
  const duplicado = async () => {
    if (sel.size === 0) return
    setAccionMsg('')
    try {
      const { data } = await notasVentaApi.archivar([...sel], 'duplicado')
      setSel(new Set())
      setAccionMsg(`${data.archivadas} descartada${data.archivadas === 1 ? '' : 's'} como duplicado.`)
      await cargar()
    } catch { setAccionMsg('No se pudo descartar.') }
  }
  const fraude = async () => {
    if (sel.size === 0) return
    if (!window.confirm(`¿Confirmás FRAUDE para ${sel.size} NV? Se cortan los mails, se archivan y sus clientes van a la LISTA NEGRA (no van a poder operar más).`)) return
    setAccionMsg('')
    try {
      const { data } = await notasVentaApi.archivar([...sel], 'fraude')
      setSel(new Set())
      setAccionMsg(`${data.archivadas} descartada${data.archivadas === 1 ? '' : 's'} como FRAUDE — clientes en lista negra.`)
      await cargar()
    } catch { setAccionMsg('No se pudo descartar.') }
  }
  const desarchivar = async () => {
    if (sel.size === 0) return
    setAccionMsg('')
    try {
      const { data } = await notasVentaApi.desarchivar([...sel])
      setSel(new Set())
      setAccionMsg(`${data.desarchivadas} NV desarchivada${data.desarchivadas === 1 ? '' : 's'}.`)
      await cargar()
    } catch { setAccionMsg('No se pudo desarchivar.') }
  }

  const toggleEstado = (e) => {
    setFiltros(f => ({
      ...f,
      estados: f.estados.includes(e) ? f.estados.filter(x => x !== e) : [...f.estados, e],
    }))
    setPage(1); limpiarSel()
  }
  const setFiltro = (k) => (e) => { setFiltros(f => ({ ...f, [k]: e.target.value })); setPage(1); limpiarSel() }
  const limpiar = () => { setFiltros(FILTROS_DEFAULT); setPage(1); limpiarSel() }
  const irPagina = (p) => { setPage(p); limpiarSel() }

  // La lista propia del vendedor es su vista NORMAL, no un "filtro activo":
  // solo cuenta como filtro cuando difiere del canal del usuario.
  const hayFiltros = Boolean(
    filtros.q || filtros.articulo || filtros.entrega || filtros.email || filtros.ip || filtros.fecha_desde || filtros.fecha_hasta || filtros.estados.length > 0 || filtros.sin_completos || filtros.con_saldo || filtros.vendedor_id
    || (filtros.lista !== undefined && (filtros.lista || '') !== (user?.lista_precios || ''))
  )

  // hasAnyRole y no user.rol: un usuario puede tener mas de un rol y
  // alcanza con que ALGUNO habilite la accion (caso Fatima: administracion
  // principal + ventas adicional).
  const puedeCrear = rolOperativoVentas()

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Notas de Venta</h2>
        {puedeCrear && (
          <div style={{ display: 'flex', gap: space.sm }}>
            {/* Plan B: lo facturado a mano en Presea entra como NV de LGR. */}
            <Boton variante="secondary" onClick={() => setImportarPresea(true)}
              title="Trae las ventas hechas a mano en Presea (factura + remito + recibo) como NV de LGR, listas para logística">
              ⬇ Importar desde Presea
            </Boton>
            <Boton variante="primary" onClick={() => navigate('/notas-venta/nueva')}>
              + Nueva NV
            </Boton>
          </div>
        )}
      </div>

      {importarPresea && (
        <ModalImportarPresea onClose={() => setImportarPresea(false)} onImportado={() => cargar({ silent: true })} />
      )}

      {/* Filtros en DOS pisos (Martin, 9 sep 2026):
          piso 1 = los buscadores de texto, en el orden de siempre;
          piso 2 = fechas · lista · ocultar completas + limpiar · chips de
          estado · acciones sobre las filas tildadas · dejadas · archivadas. */}
      <div style={styles.filtros}>
        <div style={styles.piso}>
          <input style={{ ...styles.filtroInput, flex: 2 }} placeholder="Buscar N° LGR / factura / remito / pedido web / cliente..." value={filtros.q} onChange={setFiltro('q')} />
          {/* Sug #63: qué pedidos LLEVAN tal producto / a DÓNDE se entregan. */}
          <input style={{ ...styles.filtroInput, flex: 1 }} placeholder="Producto (código o nombre)..." value={filtros.articulo || ''} onChange={setFiltro('articulo')} title="NVs que llevan este artículo" />
          <input style={{ ...styles.filtroInput, flex: 1 }} placeholder="Entrega (dirección o localidad)..." value={filtros.entrega || ''} onChange={setFiltro('entrega')} title="Dirección de envío de la NV o domicilio del cliente" />
          {/* Sug #64: rastreo de fraude — todos los pedidos del mismo mail o IP. */}
          <input style={{ ...styles.filtroInput, flex: 1 }} placeholder="Email del comprador..." value={filtros.email || ''} onChange={setFiltro('email')} title="Email de la ficha del cliente o del pedido web" />
          <input style={{ ...styles.filtroInput, flex: 1 }} placeholder="IP del pedido web..." value={filtros.ip || ''} onChange={setFiltro('ip')} title="IP desde la que se hizo el pedido en la tienda (solo pedidos del storefront)" />
        </div>

        <div style={styles.piso}>
          <input style={styles.filtroInput} type="date" value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} title="Fecha desde" />
          <input style={styles.filtroInput} type="date" value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} title="Fecha hasta" />
          <select style={styles.filtroInput} value={filtros.lista ?? ''} onChange={setFiltro('lista')} title="Canal: lista de precios del cliente">
            <option value="">Todas las listas</option>
            {listas.map(l => (
              <option key={l.codigo} value={l.codigo}>
                {l.codigo}{user?.lista_precios === l.codigo ? ' (mi lista)' : ''}
              </option>
            ))}
          </select>
          <select style={styles.filtroInput} value={filtros.vendedor_id || ''} onChange={setFiltro('vendedor_id')} title="Pedidos del vendedor (en los pedidos web, quien lo tomó)">
            <option value="">Todos los vendedores</option>
            {vendedores.map(v => (
              <option key={v.id} value={v.id}>{v.nombre}{user?.id === v.id ? ' (yo)' : ''}</option>
            ))}
          </select>
          {/* Excluir las C=COMPLETO (factura+recibo+remito): el vendedor ve solo
              los pedidos que todavía tienen alguna tarea pendiente. */}
          {!verArchivadas && (
            <label style={styles.toggleCompletas}>
              <input
                type="checkbox"
                checked={!!filtros.sin_completos}
                onChange={() => { setFiltros(f => ({ ...f, sin_completos: !f.sin_completos })); setPage(1); limpiarSel() }}
              />
              Ocultar completas <TagRedondo letra="C" color="#166534" lleno title="COMPLETO: factura + recibo + remito" />
            </label>
          )}
          <label style={styles.toggleCompletas} title="NV con cobro vigente que no cubre el monto a cobrar (sugerencia #76)">
            <input
              type="checkbox"
              checked={!!filtros.con_saldo}
              onChange={() => { setFiltros(f => ({ ...f, con_saldo: !f.con_saldo })); setPage(1); limpiarSel() }}
            />
            Con saldo <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#f97316' }} />
          </label>
          <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={limpiar}>Limpiar</Boton>

          {!verArchivadas && (
            <>
              <span style={styles.sep} />
              <EstadoChipsFilter options={ESTADO_OPCIONES} selected={filtros.estados} onToggle={toggleEstado} style={{ margin: 0 }} />
            </>
          )}

          {/* Acciones sobre las filas tildadas — SIEMPRE visibles (Martin, 8 sep:
              "sigo sin ver los botones"): se habilitan al tildar filas en borrador. */}
          <span style={styles.sep} />
          <div style={styles.bulkBar}>
            <span style={sel.size === 0 ? { color: '#64748b' } : {}}>
              {sel.size > 0
                ? <><strong>{sel.size}</strong> seleccionada{sel.size === 1 ? '' : 's'} →</>
                : 'Tildá filas en borrador para:'}
            </span>
            {verArchivadas ? (
              <Boton variante="secondary" sm disabled={sel.size === 0} onClick={desarchivar}>Desarchivar{sel.size > 0 ? ` (${sel.size})` : ''}</Boton>
            ) : verDejadas ? (
              <Boton variante="secondary" sm disabled={sel.size === 0} onClick={retomar}>↩ Retomar{sel.size > 0 ? ` (${sel.size})` : ''}</Boton>
            ) : (
              <>
                <Boton variante="secondary" sm disabled={sel.size === 0} title="Sale de tu listado; los mails de recupero siguen" onClick={dejar}>🤖 Dejar al sistema{sel.size > 0 ? ` (${sel.size})` : ''}</Boton>
                <Boton variante="secondary" sm disabled={sel.size === 0} title="Descarta como duplicado: corta los mails y sale de circulación" onClick={duplicado}>📄 Duplicado{sel.size > 0 ? ` (${sel.size})` : ''}</Boton>
                <Boton variante="destructive" sm disabled={sel.size === 0} title="Descarta como FRAUDE: corta los mails y manda los clientes a la LISTA NEGRA" onClick={fraude}>🚫 Fraude{sel.size > 0 ? ` (${sel.size})` : ''}</Boton>
              </>
            )}
            {sel.size > 0 && <Boton variante="ghost" sm onClick={() => setSel(new Set())}>Cancelar</Boton>}
          </div>

          <div style={{ flex: 1 }} />
          <Boton
            variante={verDejadas ? 'secondary' : 'ghost'}
            title="Pedidos que dejaste al sistema: salieron de tu listado pero el recupero sigue mandando sus mails (escalera de 72 h + 2 avisos finales los días 5 y 6). Si pagan vuelven solos a activas; si no, se archivan solos."
            onClick={() => { setVerDejadas(v => !v); setVerArchivadas(false); setPage(1); limpiarSel() }}
          >
            {verDejadas ? '← Volver a activas' : `🤖 Dejadas al sistema (${dejadasCount})`}
          </Boton>
          <Boton variante={verArchivadas ? 'secondary' : 'ghost'} onClick={() => { setVerArchivadas(v => !v); setVerDejadas(false); setPage(1); limpiarSel() }}>
            {verArchivadas ? '← Volver a activas' : '🗄 Ver archivadas'}
          </Boton>
        </div>
      </div>

      <div style={styles.info}>
        {!loading && !error && (
          <span>
            {paginacion.total} NV {verArchivadas ? 'archivadas' : verDejadas ? 'dejadas al sistema (los mails de recupero siguen; si pagan vuelven solas a activas)' : 'encontradas'}
            {hayFiltros && !verArchivadas && <strong style={styles.filtrosActivos}> · filtros activos</strong>}
          </span>
        )}
        {accionMsg && <span style={styles.accionMsg}>· {accionMsg}</span>}
      </div>

      <div style={{ ...styles.tableWrapper, ...(loading && nvs.length > 0 ? { opacity: 0.6, transition: 'opacity .15s' } : {}) }}>
        {loading && nvs.length === 0 ? (
          <p style={styles.empty}>Cargando...</p>
        ) : error ? (
          <p style={styles.empty}>{error}</p>
        ) : nvs.length === 0 ? (
          <div style={styles.empty}>
            {hayFiltros ? (
              <>
                <div>No hay notas de venta que coincidan con los filtros.</div>
                <Boton variante="secondary" sm style={{ marginTop: space.md }} onClick={limpiar}>
                  Limpiar filtros
                </Boton>
              </>
            ) : 'No hay notas de venta.'}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 34, textAlign: 'center' }}>
                  <input type="checkbox" checked={todasSel} onChange={toggleTodas}
                    disabled={seleccionables.length === 0} title="Seleccionar todas las de esta página" />
                </th>
                {['N° LGR', 'NV Presea', 'Factura', 'Rem', 'Estado', 'F·P·R', 'Pago', 'Código', 'Cliente', 'Fecha', 'Tipo', 'Vendedor', 'Items', 'Total', ''].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nvs.map(nv => (
                <FilaNV key={nv.id} nv={nv} sel={sel} styles={styles}>
                  <td style={{ ...styles.td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={sel.has(nv.id)} disabled={!esSeleccionable(nv)}
                      onChange={() => toggleSel(nv.id)}
                      title={esSeleccionable(nv) ? '' : 'Solo se archivan NV en borrador'} />
                  </td>
                  <td style={styles.tdMono}>
                    <LinkDoc to={`/notas-venta/${nv.id}`}>{nv.numero_lgr}</LinkDoc>
                    {/* Número del pedido web (sug #93): el cliente reclama con ese. */}
                    {nv.origen !== 'lgr' && nv.origen_externo_id && (
                      <span style={styles.pedidoWeb} title="Número del pedido web">{nv.origen_externo_id}</span>
                    )}
                    {/* El comprador dejó una nota en el checkout: el globo la
                        anuncia en el listado y el texto completo vive en el
                        banner del detalle (y en el tooltip para leer al paso). */}
                    {nv.nota_cliente && (
                      <span
                        title={`Nota del cliente: ${nv.nota_cliente}`}
                        style={{ marginLeft: 6, cursor: 'help', fontSize: 13 }}
                      >💬</span>
                    )}
                    {/* Posible fraude (RiesgoPagoService): el motivo en el tooltip, el detalle en la NV. */}
                    {nv.riesgo_motivos?.length > 0 && (
                      <span
                        title={`Posible fraude:\n${nv.riesgo_motivos.join('\n')}`}
                        style={{ marginLeft: 6, cursor: 'help', fontSize: 11, fontWeight: 600, color: colors.err, background: colors.errBg, border: `1px solid ${colors.errBorder}`, borderRadius: 4, padding: '0 5px', fontFamily: 'inherit' }}
                      >⚠ Posible fraude</span>
                    )}
                    {/* Membresía + producto restringido por fraude pagado por Mobbex: revisar. */}
                    {nv.pago_restringido_aviso && (
                      <span
                        title={nv.pago_restringido_aviso}
                        style={{ marginLeft: 6, cursor: 'help', fontSize: 11, fontWeight: 600, color: colors.warn, background: colors.warnBg, border: `1px solid ${colors.warnStrong}`, borderRadius: 4, padding: '0 5px', fontFamily: 'inherit' }}
                      >🔒 Revisar pago</span>
                    )}
                    {/* Mismo cliente con otro pedido en las 72 h anteriores (PedidoRepetidoService). */}
                    {nv.repite_pedido && (
                      <span
                        title="El mismo cliente (email, teléfono, nombre o IP) hizo otro pedido en las 72 h anteriores. El detalle dice cuál."
                        style={{ marginLeft: 6, cursor: 'help', fontSize: 11, fontWeight: 600, color: colors.warn, background: colors.warnBg, border: `1px solid ${colors.warnStrong}`, borderRadius: 4, padding: '0 5px', fontFamily: 'inherit' }}
                      >🔁 Repite</span>
                    )}
                  </td>
                  <td style={styles.tdMono}>
                    {nv.numero_nv_presea
                      ? nv.numero_nv_presea
                      : <span style={styles.na}>—</span>}
                  </td>
                  <td style={styles.tdMono}>
                    {nv.numero_presea && nv.numero_presea !== '0'
                      ? nv.numero_presea
                      : <span style={styles.na}>—</span>}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    {(() => {
                      // Columna PROPIA del despacho del remito (estado_vigente,
                      // la fuente única) — separada del estado de la NV, que es
                      // otra cosa (feedback Martin 13 ago). Tag ABREVIADO para
                      // no comer ancho en pantallas chicas; nombre completo en
                      // el tooltip. Sin remito → vacío.
                      const rVig = (nv.remitos || []).find(r => r.estado !== 'anulado')
                      const ev = rVig?.estado_vigente
                      if (!ev) return <span style={styles.na}>—</span>
                      const abrev = REMITO_ABREV[ev.codigo] || (ev.etiqueta || '').slice(0, 4).toUpperCase()
                      return (
                        <span
                          title={`Remito: ${ev.etiqueta}`}
                          style={{ display: 'inline-block', padding: '2px 5px', borderRadius: 4, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', color: ev.color, background: ev.bg, cursor: 'default' }}
                        >{abrev}</span>
                      )
                    })()}
                  </td>
                  <td style={styles.td}><EstadoBadge estado={nv.estado} /></td>
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}><CicloTags ciclo={nv.ciclo} /></td>
                  <td style={{ ...styles.td, textAlign: 'center' }}><PagoDot estado={nv.pago_estado} nv={nv} /></td>
                  <td style={styles.tdMono}>{nv.cliente?.codigo_presea || '—'}</td>
                  <td style={styles.td}>
                    <span style={styles.principal}>{nv.cliente?.nombre}</span>
                    {/* Con el filtro de canal activo, una NV propia de OTRO canal
                        se ve igual (el backend la incluye); el chip dice por qué. */}
                    {filtros.lista && nv.cliente?.lista_precios && nv.cliente.lista_precios !== filtros.lista && (
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', color: colors.textMeta, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '0 4px' }} title="NV tuya de otro canal: se muestra aunque el filtro de lista esté activo">
                        {nv.cliente.lista_precios}
                      </span>
                    )}
                  </td>
                  <td style={styles.tdFecha}>
                    {fmtFecha(nv.fecha)}
                    {/* Hora del pedido (created_at en hora ART) — la fecha de la
                        NV puede estar retro-datada; la hora es de la CARGA. */}
                    <span style={{ marginLeft: 6, color: colors.textPlh }}>{fmtHoraDe(nv.created_at)}</span>
                  </td>
                  <td style={styles.td}>
                    {(() => {
                      // Badge de letra A/B/C — mismo patrón que /facturas y
                      // NotaVentaDetallePage (regla Martin 19 jun: aplicar
                      // el cambio del tipo en el listado de NV también).
                      const letra = nv.tipo_factura || ''
                      const col = letra === 'A' ? { bg: '#eff6ff', color: '#1d4ed8' }
                                : letra === 'B' ? { bg: '#dcfce7', color: '#166534' }
                                : letra === 'C' ? { bg: '#fef3c7', color: '#92400e' }
                                : { bg: '#f1f5f9', color: '#64748b' }
                      return (
                        <span style={{
                          background: col.bg,
                          color: col.color,
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          fontFamily: 'ui-monospace, monospace',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}>{letra || '—'}</span>
                      )
                    })()}
                  </td>
                  <td style={styles.tdSec}>
                    {/* Pedido web: se ve quién lo TOMÓ (procesó) — el vendedor
                        de la venta sigue siendo el 121 WEB SITE. Manual: el
                        vendedor de siempre. */}
                    {nv.origen !== 'lgr'
                      ? (nv.tomada_por || <span style={{ color: '#b45309', fontWeight: 600 }}>Sin tomar</span>)
                      : nv.vendedor?.nombre}
                  </td>
                  <td style={{ ...styles.tdMono, textAlign: 'center' }}>{nv.items_count}</td>
                  {/* Total = lo que se factura y se cobra, CON percepciones (monto_a_cobrar:
                      la factura de Presea, o renglones + percepciones estimadas
                      si todavía no se facturó). El mismo número que la NV —
                      sugerencia #101. */}
                  <td style={{ ...styles.tdMono, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtMoney(nv.monto_a_cobrar ?? nv.total)}</td>
                  <td style={styles.tdAccion}>
                    {nv.es_editable && (
                      <Boton variante="secondary" sm onClick={(e) => { e.stopPropagation(); navigate(`/notas-venta/${nv.id}/editar`) }}>Editar</Boton>
                    )}
                  </td>
                </FilaNV>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Paginador pagina={paginacion.current_page} totalPaginas={paginacion.last_page} onCambiar={irPagina} />
    </div>
  )
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  filtros: { display: 'flex', flexDirection: 'column', gap: space.sm, marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  piso: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', width: '100%' },
  sep: { width: 1, height: 22, background: colors.border, margin: `0 ${space.xs}px` },
  filtroInput: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', minWidth: 130, background: colors.bg },
  info: { marginBottom: space.md, fontSize: text.sec, color: colors.textSec, display: 'flex', gap: space.sm, flexWrap: 'wrap' },
  filtrosActivos: { color: colors.link, fontWeight: 600 },
  toggleCompletas: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: text.sec, color: colors.textSec, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  accionMsg: { color: '#166534', fontWeight: 600 },
  bulkBar: { display: 'inline-flex', alignItems: 'center', gap: space.sm, padding: `4px ${space.md}px`, background: colors.linkBg, border: '1px solid #bfdbfe', borderRadius: radius.sm, fontSize: text.sec, flexWrap: 'wrap' },

  tableWrapper: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 16px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  trSel: { background: colors.linkBg },
  td: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.2 },
  pedidoWeb: { marginLeft: 6, fontSize: text.sec, color: colors.textSec, fontWeight: 400 },
  tdMono: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdFecha: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdSec: { padding: '0 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdAccion: { padding: '0 16px', verticalAlign: 'middle', textAlign: 'right' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  na: { color: colors.textPlh },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },

  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' },
  badgeFact: { background: colors.bgSoft, color: colors.textSec, fontFamily: font.mono },

  paginacion: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.xl, marginTop: space.xl, fontSize: text.body, color: colors.textSec },
  pagBtn: { padding: '6px 14px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, background: colors.bg, cursor: 'pointer', fontSize: text.body, color: colors.text },
}
